import { FC } from 'react';
import { Badge } from '@components/atoms/Badge';
import { useTranslation } from '@hooks/useTranslation';
import { OrganizationSubscriptionStatus } from '@models/subscription';
import { formatDateOnly } from '@utils/formatDate';
import { getSubscriptionStatusConfig } from '@utils/subscriptionStatusConfig';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Whole days until `trialEndsAt`, rounded up like `useTrialStatus` (a partial day still
 * counts as a day left). Null when there is no usable end date.
 */
export const getTrialDaysLeft = (
  trialEndsAt?: string | null,
  now: number = Date.now(),
): number | null => {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - now) / MS_PER_DAY));
};

interface SubscriptionStatusBadgeProps {
  status?: OrganizationSubscriptionStatus | string | null;
  trialEndsAt?: string | null;
  trialExpiredAt?: string | null;
  /** Render the "Ends ..." / "Expired on ..." line under the badge. Default true. */
  showSubLine?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

/** Subscription status pill plus the trial date line, for the super-admin Organizations page. */
const SubscriptionStatusBadge: FC<SubscriptionStatusBadgeProps> = ({
  status,
  trialEndsAt,
  trialExpiredAt,
  showSubLine = true,
  size = 'sm',
}) => {
  const tEnumLabels = useTranslation('enumLabels');
  const tOrganizations = useTranslation('organizations');

  if (!status) {
    return (
      <span className="text-sm text-gray-400 italic">
        {tOrganizations.noSubscription}
      </span>
    );
  }

  const config = getSubscriptionStatusConfig(status, tEnumLabels);
  const Icon = config.icon;

  let subLine: string | null = null;
  let urgent = false;

  if (showSubLine && status === OrganizationSubscriptionStatus.TRIAL) {
    const days = getTrialDaysLeft(trialEndsAt);
    if (days !== null) {
      const date = formatDateOnly(trialEndsAt);
      urgent = days <= 3;
      subLine =
        days === 0
          ? tOrganizations.trialEndsLineToday.replace('{date}', date)
          : days === 1
            ? tOrganizations.trialEndsLineOneDay.replace('{date}', date)
            : tOrganizations.trialEndsLine
                .replace('{date}', date)
                .replace('{n}', String(days));
    }
  } else if (
    showSubLine &&
    status === OrganizationSubscriptionStatus.TRIAL_EXPIRED
  ) {
    const date = formatDateOnly(trialExpiredAt ?? trialEndsAt);
    if (date) subLine = tOrganizations.trialExpiredLine.replace('{date}', date);
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <Badge size={size} variant={config.badgeVariant}>
        <Icon className="w-3 h-3 mr-1" aria-hidden="true" />
        {config.text}
      </Badge>
      {subLine && (
        <span
          className={`text-xs ${
            urgent
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-gray-500 dark:text-gray-400'
          }`}>
          {subLine}
        </span>
      )}
    </div>
  );
};

export default SubscriptionStatusBadge;
